<template>
    <canvas
        ref="canvas"
        @click="onClick"
    />
</template>
<script>
import {
    ref, onMounted, computed,
    watch, shallowRef,
} from 'vue';
import { useIntersectionObserver } from '../../assets/js/vue/utlis/useIntersectionObserver';
import { useRafFps } from '../../assets/js/vue/utlis/useRafFps';
import { useMouse } from '../../assets/js/vue/utlis/useMouse';
import { useIsRetina } from '../../assets/js/vue/utlis/useIsRetina';
import { useStore } from '../../store';

export default {
    name: 'PrivateHeroBg',
    props: {
        lights: {
            type: Array,
            default: () => [],
        },
    },
    setup(props, { emit }) {
        const width = 1920;
        const height = 1252;
        const width_mob = 360;
        const height_mob = 594;
        const ctx = shallowRef();
        const canvas = shallowRef(null);
        const background = new Image();
        let scale = 1;
        const store = useStore();
        const isMobile = computed(() => store.state.app.isMobile);
        const innerWidth = computed(() => store.state.app.windowWidth);

        // мерцание точек
        let track1 = 0;
        const speed1 = 6; // loop animation length in sec

        // бегущая точка
        let track2 = 0;
        const speed2 = 3; // loop animation length in sec

        const fps = 60;
        const showPointsAfter = 2000 // in ms
        const maxPointsOpacity = 0.25;
        let pointsOpacity = 0;
        let allowedChangePointsOpacity = false;

        let lights = [];
        watch(props.lights, (val) => {
            lights = val.map((item) => ({
                deg: item.deg,
                r: item.r,
                offset: Math.random(),
                paths: [],
            }));
        }, { immediate: true });

        const mouse = useMouse();

        watch([mouse.x, mouse.y], ([xRaw, yRaw ]) => {
            const { top, left } = canvas.value.getBoundingClientRect();
            const x = xRaw - left;
            const y = yRaw - top;

            const isInPath = lights.some(({ paths }) => paths.some((path) => ctx.value.isPointInPath(path, x, y)));

            canvas.value.style.cursor = isInPath ? 'pointer' : 'default';
        });

        const onClick = () => {
            const { top, left } = canvas.value.getBoundingClientRect();
            const x = mouse.x.value - left;
            const y = mouse.y.value - top;

            const i = lights.findIndex(({ paths }) => paths.some((path) => ctx.value.isPointInPath(path, x, y)));

            if (i > -1) {
                emit('click', i);
            }
        };

        const targetIsVisible = ref(false);
        useIntersectionObserver(
            canvas,
            ([{ isIntersecting }]) => {
                targetIsVisible.value = isIntersecting
                emit('in-viewport', isIntersecting);
            },
        );

        const { isRetina } = useIsRetina();

        const updBg = () => {
            const postfix = isRetina.value ? '@2' : '';
            background.src = isMobile.value ? `/local/templates/redesign/src/assets/img/private/hero/hero-bg_mob${postfix}.webp` : `/local/templates/redesign/src/assets/img/private/hero/hero-bg${postfix}.webp`;
        };

        watch(isMobile, () => {
            updBg();
        }, { immediate: true });

        const drawBg = () => {
            const ratio = background.naturalWidth / background.naturalHeight;
            const width = canvas.value.width;
            const height = width / ratio;
            ctx.value.drawImage(background, 0, 0, width, height);
        };

        const init = () => {
            ctx.value = canvas.value.getContext('2d');
            ctx.value.imageSmoothingQuality = 'high';
        };

        const drawPoint = () => {
            const deg = 360 * Math.min(track1, 0.5) / 0.5;
            const r = isMobile.value ? 159 : 338;
            const x0 = isMobile.value ? 180 : 960;
            const y0 = isMobile.value ? 348 : 696;
            const d = (isMobile.value ? 4 : 9) * scale;
            const x = (Math.cos(deg*Math.PI/180) * r + x0)*scale;
            const y = (Math.sin(-deg*Math.PI/180) * r + y0)*scale;

            ctx.value.save();
            if (deg > 0 && deg <= 45) {
                ctx.value.globalAlpha = deg/45;
            } else if (deg > 45 && deg <= 180) {
                ctx.value.globalAlpha = 1;
            } else if (deg > 180 && deg <= 225) {
                ctx.value.globalAlpha = -deg/45 + 5;
            } else {
                ctx.value.globalAlpha = 0;
            }

            ctx.value.beginPath();
            ctx.value.arc(x, y, d/2, 0, 2 * Math.PI);
            ctx.value.fillStyle = '#878584';
            ctx.value.fill();
            ctx.value.restore();
        };

        const drawLights = () => {
            const x0 = 960;
            const y0 = 696;
            const d1 = 24 * scale;
            const d2 = 48 * scale;
            const d3max = 73 * scale;

            lights.forEach((item) => {
                const deg = item.deg + 90;
                const track = (track2 + item.offset + 1) % 1;
                const d3 = d3max * Math.sin(track * Math.PI);
                const r = item.r;

                const x = (Math.cos(deg*Math.PI/180) * r + x0)*scale;
                const y = (Math.sin(-deg*Math.PI/180) * r + y0)*scale;

                item.paths[0] = new Path2D();
                item.paths[0].arc(x, y, d1/2, 0, 2 * Math.PI);
                ctx.value.fillStyle = `rgba(255, 163, 96, ${pointsOpacity})`;
                ctx.value.fill(item.paths[0]);

                item.paths[1] = new Path2D();
                item.paths[1].arc(x, y, d2/2, 0, 2 * Math.PI);
                ctx.value.fillStyle = `rgba(255, 163, 96, ${pointsOpacity * maxPointsOpacity})`;
                ctx.value.strokeStyle = `rgba(255, 163, 96, ${pointsOpacity})`;
                ctx.value.lineWidth = 1.2;
                ctx.value.fill(item.paths[1]);
                ctx.value.stroke(item.paths[1]);

                item.paths[2] = new Path2D();
                item.paths[2].arc(x, y, d3/2, 0, 2 * Math.PI);
                ctx.value.fillStyle = `rgba(255, 163, 96, ${pointsOpacity * maxPointsOpacity})`;
                ctx.value.fill(item.paths[2]);
            });
        };

        const draw = () => {
            ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height);
            drawBg();
            if (track1 < 1) track1 = Math.min(track1 + 1/speed1/fps, 1); else track1 = 0;
            if (track2 < 1) track2 = Math.min(track2 + 1/speed2/fps, 1); else track2 = 0;
            drawPoint();
            if (!isMobile.value) drawLights();
            if (allowedChangePointsOpacity && pointsOpacity < 1) pointsOpacity = Math.min(pointsOpacity + 1/1/fps, 1);
        };

        const pause = computed(() => !targetIsVisible.value);
        useRafFps(() => {
            draw();
        }, pause, fps);

        watch([isMobile, innerWidth, ctx], ([isMobile, innerWidth, ctx]) => {
            if (!ctx) return;

            if (isMobile) {
                scale = innerWidth / 360;
            } else {
                scale = innerWidth / 1920;
            }
            ctx.canvas.width = isMobile ? width_mob * scale : width * scale;
            ctx.canvas.height = isMobile ? height_mob * scale : height * scale;
            draw();
        }, { immediate: true });

        onMounted(() => {
            init();

            setTimeout(() => {
                allowedChangePointsOpacity = true;
            }, showPointsAfter);
        });

        return {
            canvas,
            scale,
            onClick,
        };
    },
};
</script>
<style lang="scss">

</style>
